import React, {useEffect, useMemo, useRef} from 'react';
import {
  ScrollView,
  TouchableOpacity,
  View,
  StyleSheet,
  Platform,
} from 'react-native';
import CCText from '../CCText';
import ContextMenu from '@/Components/ContextMenu';
import usePromoteBranch, {
  PromoteBranchPayload,
} from '@/Hooks/ManageMoves/useManageMoves';

/* ─── Design Tokens ───────────────────────────────────────── */
const C = {
  brown: '#4D3F37',
  brownDark: '#262322',
  brownMedium: '#6b584d',
  cream: '#FAF6EB',
  yellow: '#FACF47',
  teal: '#3DAB9E',
  grey: '#A8A7A6',
  greyDark: '#7D7D7D',
  border: 'rgba(38,35,34,0.10)',
  borderMed: 'rgba(38,35,34,0.16)',
  white: '#FFFFFF',
};

const BRANCH_COLORS = ['#E17846', '#67B3E1', '#3DAB9E', '#937ADB', '#CD2C39'];

/* ─── Helpers ─────────────────────────────────────────────── */
function getMoveNumPrefix(fen: string): string {
  if (!fen) return '';
  const parts = fen.split(' ');
  const activeColor = parts[1];
  const fullmove = parseInt(parts[5], 10) || 1;
  const isWhiteMove = activeColor === 'b'; // white just moved → active is now black
  const moveNum = isWhiteMove ? fullmove : fullmove - 1;
  return isWhiteMove ? `${moveNum}. ` : `${moveNum}… `;
}

/**
 * Recursive branch helpers:
 * - branch has nodes
 * - each node can have nested branches (infinite depth)
 */
function buildBranches(children: any[], prevMove: any, depth: number) {
  const buildBranchFromRoot = (
    rootMove: any,
    rootPrevMove: any,
    branchDepth: number,
    branchIndex: number,
  ) => {
    const nodes: any[] = [];
    let cursor = rootMove;
    let cursorPrev = rootPrevMove;

    // Keep a single line for this branch by following first child as continuation.
    // Additional children at each node become nested branches at that split point.
    while (cursor) {
      const childMoves = cursor?.child_moves || [];
      const continuation = childMoves[0];
      const splitBranches = childMoves.slice(1);

      nodes.push({
        id: cursor?.id || `${cursor?.fen || cursor?.san}-${branchDepth}`,
        san: `${getMoveNumPrefix(cursor?.fen)}${cursor?.san || ''}`,
        move: cursor,
        prevMove: cursorPrev,
        branches: buildBranches(splitBranches, cursor, branchDepth + 1),
      });

      if (!continuation) {
        break;
      }
      cursorPrev = cursor;
      cursor = continuation;
    }

    const leaf = nodes[nodes.length - 1]?.move;
    return {
      id: `br-${leaf?.id || `${branchDepth}-${branchIndex}`}`,
      color: BRANCH_COLORS[(branchDepth + branchIndex) % BRANCH_COLORS.length],
      nodes,
      annotation: leaf?.annotation || '',
    };
  };

  return (children || []).map((child, idx) =>
    buildBranchFromRoot(child, prevMove, depth, idx),
  );
}

/* ─── MoveChip ────────────────────────────────────────────── */
function MoveChip({
  san,
  isActive,
  isEmpty,
  annotationIcon,
  kind = 'main',
  onPress,
}: {
  san?: string;
  isActive: boolean;
  isEmpty?: boolean;
  annotationIcon?: string;
  kind?: 'main' | 'branch';
  onPress?: () => void;
}) {
  if (isEmpty || !san) {
    return (
      <View style={styles.moveChipEmpty}>
        <CCText style={styles.moveChipEmptyText}>—</CCText>
      </View>
    );
  }
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      style={[
        styles.moveChip,
        kind === 'main' ? styles.moveChipMain : styles.moveChipBranch,
        isActive && styles.moveChipActive,
      ]}>
      <CCText
        style={[styles.moveChipText, isActive && styles.moveChipTextActive]}>
        {san}
      </CCText>
      {isActive && annotationIcon && (
        <View style={styles.annotationBadge}>
          <CCText style={styles.annotationBadgeText}>∞</CCText>
        </View>
      )}
    </TouchableOpacity>
  );
}

/* ─── BranchGroup (recursive) ─────────────────────────────── */
function BranchGroup({
  branches,
  activeMove,
  onMoveClick,
  onPromote,
  onDelete,
  depth = 0,
}: {
  branches: any[];
  activeMove: any;
  onMoveClick: (move: any, prevMove: any) => void;
  onPromote: (moveId?: any) => void;
  onDelete: (moveId?: any) => void;
  depth?: number;
}) {
  if (!branches?.length) return null;

  return (
    <View style={[styles.branchTree, depth === 0 && styles.branchTreeRoot]}>
      {branches.map((branch, bi) => {
        const isLast = bi === branches.length - 1;
        return (
          <View
            key={branch.id || `${depth}-${bi}`}
            style={[styles.branchRow, !isLast && styles.branchRowGap]}>
            <View style={styles.connectorCol}>
              <View
                style={[
                  styles.connectorVertical,
                  {backgroundColor: branch.color},
                  isLast && styles.connectorVerticalLast,
                ]}
              />
              <View
                style={[
                  styles.connectorTick,
                  {backgroundColor: branch.color},
                ]}
              />
            </View>

            <View style={styles.branchContent}>
              <View style={styles.branchNodesWrap}>
                {branch.nodes?.map((node, ni) => (
                  <React.Fragment key={node.id || `node-${ni}`}>
                    <ContextMenu
                      menuOptions={[
                        {label: 'Promote', onClick: () => onPromote(node.move?.id)},
                        {label: 'Delete', onClick: () => onDelete(node.move?.id)},
                      ]}>
                      <View style={styles.branchChipWrapper}>
                        <MoveChip
                          san={node.san}
                          isActive={
                            activeMove?.id
                              ? activeMove.id === node.move?.id
                              : activeMove?.fen === node.move?.fen
                          }
                          kind="branch"
                          onPress={() => onMoveClick(node.move, node.prevMove)}
                        />
                      </View>
                    </ContextMenu>

                    {node.branches?.length > 0 && (
                      <View style={styles.branchNested}>
                        <BranchGroup
                          branches={node.branches}
                          activeMove={activeMove}
                          onMoveClick={onMoveClick}
                          onPromote={onPromote}
                          onDelete={onDelete}
                          depth={depth + 1}
                        />
                      </View>
                    )}
                  </React.Fragment>
                ))}
              </View>

              {!!branch.annotation && (
                <View style={styles.branchAnnotationInline}>
                  <View
                    style={[
                      styles.branchAnnotationDot,
                      {backgroundColor: branch.color},
                    ]}
                  />
                  <CCText style={styles.branchAnnotationText}>
                    {branch.annotation}
                  </CCText>
                </View>
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
}

/* ─── MoveRow ─────────────────────────────────────────────── */
function MoveRow({
  number,
  whiteMove,
  blackMove,
  activeMove,
  onWhitePress,
  onBlackPress,
}: {
  number: number;
  whiteMove?: any;
  blackMove?: any;
  activeMove: any;
  onWhitePress: () => void;
  onBlackPress: () => void;
}) {
  const isWhiteActive = activeMove?.id
    ? activeMove.id === whiteMove?.id
    : activeMove?.fen === whiteMove?.fen;
  const isBlackActive = activeMove?.id
    ? activeMove.id === blackMove?.id
    : activeMove?.fen === blackMove?.fen;

  return (
    <View style={styles.moveRow}>
      <CCText style={styles.moveNum}>{number}.</CCText>
      <View style={styles.moveRowMoves}>
        <MoveChip
          san={whiteMove?.san || whiteMove?.move}
          isActive={isWhiteActive}
          isEmpty={!whiteMove?.san && !whiteMove?.move}
          annotationIcon={whiteMove?.annotation_icon}
          kind="main"
          onPress={onWhitePress}
        />
        <MoveChip
          san={blackMove?.san || blackMove?.move}
          isActive={isBlackActive}
          isEmpty={!blackMove?.san && !blackMove?.move}
          annotationIcon={blackMove?.annotation_icon}
          kind="main"
          onPress={onBlackPress}
        />
      </View>
    </View>
  );
}

/* ─── Main Component ──────────────────────────────────────── */
interface WorkbookMovesListProps {
  gameMoves: any[];
  activeMove: any;
  setmoveSelected: (move: any) => void;
  refetchStudy?: () => void;
  setLoading?: (loading: boolean) => void;
  handleChessMove?: (move: any, arg1: boolean, arg2: string, arg3: any) => void;
}

function WorkbookMovesList({
  gameMoves,
  activeMove,
  setmoveSelected,
  refetchStudy,
  setLoading,
  handleChessMove,
}: WorkbookMovesListProps) {
  const scrollRef = useRef<ScrollView>(null);

  const {
    mutate: manageBranch,
    isLoading,
    data: promoteData,
  } = usePromoteBranch();

  useEffect(() => {
    if (isLoading !== undefined) setLoading?.(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (promoteData) refetchStudy?.();
  }, [promoteData]);

  useEffect(() => {
    scrollRef.current?.scrollToEnd({animated: true});
  }, [gameMoves]);

  const handleBranch = (payload: PromoteBranchPayload) => manageBranch(payload);

  const handleSelect = (move: any, prevMove: any) => {
    handleChessMove?.(move, false, '', prevMove);
    setmoveSelected({
      ...move,
      newFen: move?.fen,
      lastFen: prevMove?.fen,
      move: move?.san,
    });
  };

  /* Determine if first move is white's (need empty placeholder at index 0) */
  function isWhitesTurn(fen: string) {
    return fen?.split(' ')?.[1] === 'w';
  }

  const displayMoves =
    gameMoves?.[0]?.fen && isWhitesTurn(gameMoves[0].fen)
      ? [{} as any, ...gameMoves]
      : gameMoves ?? [];

  const branchTraversalMoves = useMemo(() => {
    const out: Array<{move: any; prevMove: any}> = [];
    const seen = new Set<string>();

    const getMoveKey = (move: any) => String(move?.id || move?.fen || '');

    const walkBranch = (move: any, prevMove: any) => {
      if (!move?.san && !move?.move) return;

      const key = getMoveKey(move);
      if (key && seen.has(key)) return;
      if (key) seen.add(key);

      out.push({move, prevMove});
      (move?.child_moves || []).forEach((child: any) => walkBranch(child, move));
    };

    displayMoves.forEach((move, index) => {
      if (!move?.san && !move?.move) return;
      const prevMove = index > 0 ? displayMoves[index - 1] : null;
      walkBranch(move, prevMove);
    });

    return out;
  }, [displayMoves]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!branchTraversalMoves.length) return;

      const currentIndex = branchTraversalMoves.findIndex(item =>
        activeMove?.id ? item.move?.id === activeMove.id : item.move?.fen === activeMove?.fen,
      );
      const safeIndex = currentIndex < 0 ? 0 : currentIndex;
      const targetIndex =
        event.key === 'ArrowRight'
          ? Math.min(branchTraversalMoves.length - 1, safeIndex + 1)
          : Math.max(0, safeIndex - 1);

      const target = branchTraversalMoves[targetIndex];
      if (!target?.move) return;

      handleSelect(target.move, target.prevMove);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }
    return undefined;
  }, [branchTraversalMoves, activeMove]);

  /* Build pairs: [{white, black}] */
  const pairs: Array<{white: any; black: any; pairIndex: number}> = [];
  let isColorMatched = 0;
  for (let i = 0; i < displayMoves.length; i++) {
    if (i % 2 === isColorMatched) {
      pairs.push({
        white: displayMoves[i],
        black: displayMoves[i + 1],
        pairIndex: Math.floor(i / 2),
      });
    }
  }

  if (!gameMoves?.length) {
    return (
      <View style={styles.empty}>
        <CCText style={styles.emptyText}>No moves yet</CCText>
      </View>
    );
  }

  return (
    <ScrollView
      ref={scrollRef}
      style={styles.scroll}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}>
      {pairs.map(({white, black, pairIndex}) => {
        const moveNum = pairIndex + 1;
        const whiteBranches = buildBranches(white?.child_moves || [], white, 0);

        return (
          <View key={pairIndex}>
            {/* Move row */}
            <MoveRow
              number={moveNum}
              whiteMove={white}
              blackMove={black}
              activeMove={activeMove}
              onWhitePress={() => {
                if (!white?.san && !white?.move) return;
                const prevIdx = pairIndex * 2 - 1;
                const prev = prevIdx >= 0 ? displayMoves[prevIdx] : undefined;
                handleSelect(white, prev);
              }}
              onBlackPress={() => {
                if (!black?.san && !black?.move) return;
                handleSelect(black, white);
              }}
            />

            {/* White move annotation text */}
            {white?.annotation && (
              <View style={styles.moveAnnotation}>
                <CCText style={styles.moveAnnotationText}>
                  {white.annotation}
                </CCText>
              </View>
            )}

            {/* Branches for white move */}
            {whiteBranches.length > 0 && (
              <View style={styles.branchGroup}>
                <BranchGroup
                  branches={whiteBranches}
                  activeMove={activeMove}
                  onMoveClick={handleSelect}
                  onPromote={moveId =>
                    handleBranch({action: 'promote', move_id: moveId})
                  }
                  onDelete={moveId =>
                    handleBranch({action: 'delete', move_id: moveId})
                  }
                />
              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
}

/* ─── Styles ──────────────────────────────────────────────── */
const shadow =
  Platform.OS === 'web'
    ? ({boxShadow: '0 2px 8px rgba(77,63,55,0.07)'} as any)
    : {
        shadowColor: '#4D3F37',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.07,
        shadowRadius: 4,
        elevation: 2,
      };

const styles = StyleSheet.create({
  scroll: {flex: 1},
  scrollContent: {paddingVertical: 8, paddingHorizontal: 10},

  empty: {flex: 1, alignItems: 'center', paddingTop: 24},
  emptyText: {fontSize: 13, color: C.grey},

  /* Move row */
  moveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 1,
    paddingHorizontal: 4,
    borderRadius: 6,
    marginVertical: 1,
  },
  moveNum: {
    width: 28,
    fontSize: 12,
    color: C.grey,
    fontWeight: '500',
    textAlign: 'right',
    paddingRight: 4,
    flexShrink: 0,
  },
  moveRowMoves: {
    flex: 1,
    flexDirection: 'row',
  },

  /* Move chip */
  moveChip: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  moveChipMain: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: C.borderMed,
    backgroundColor: C.white,
    marginRight: 4,
  },
  moveChipBranch: {
    borderWidth: 0,
    backgroundColor: 'transparent',
    marginRight: 0,
  },
  moveChipActive: {
    backgroundColor: C.yellow,
  },
  moveChipText: {
    fontSize: 13.5,
    color: C.brown,
  },
  moveChipTextActive: {
    fontWeight: '600',
    color: C.brownDark,
  },
  moveChipEmpty: {
    flex: 1,
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  moveChipEmptyText: {
    fontSize: 13,
    color: C.grey,
    fontStyle: 'italic',
  },
  annotationBadge: {
    marginLeft: 4,
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  annotationBadgeText: {
    fontSize: 11,
    color: C.brownDark,
  },

  /* Move annotation */
  moveAnnotation: {
    paddingLeft: 32,
    paddingRight: 8,
    paddingBottom: 4,
  },
  moveAnnotationText: {
    fontSize: 11.5,
    color: C.greyDark,
    fontStyle: 'italic',
  },

  /* Branch group container */
  branchGroup: {
    marginLeft: 28,
    marginBottom: 6,
    marginTop: 4,
    paddingLeft: 8,
    borderLeftWidth: 2,
    borderLeftColor: C.border,
    gap: 6,
  },

  /* Recursive branch tree */
  branchTree: {
    marginTop: 2,
    marginBottom: 2,
  },
  branchTreeRoot: {
    marginBottom: 4,
  },
  branchRow: {
    flexDirection: 'row',
  },
  branchRowGap: {
    marginBottom: 2,
  },
  connectorCol: {
    width: 16,
    flexShrink: 0,
    position: 'relative',
    marginTop: 4,
  },
  connectorVertical: {
    position: 'absolute',
    left: 5,
    top: 0,
    bottom: 0,
    width: 1.5,
    borderRadius: 1,
    opacity: 0.55,
  },
  connectorVerticalLast: {
    bottom: 10,
  },
  connectorTick: {
    position: 'absolute',
    left: 5,
    top: 9,
    width: 8,
    height: 1.5,
    borderRadius: 1,
    opacity: 0.55,
  },
  branchContent: {
    flex: 1,
    minWidth: 0,
  },
  branchNodesWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  branchChipWrapper: {
    marginBottom: 1,
  },
  branchNested: {
    width: '100%',
    paddingLeft: 6,
    marginTop: 1,
  },
  branchAnnotationInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 3,
    marginLeft: 4,
    marginBottom: 2,
  },

  /* Branch annotations */
  branchAnnotations: {
    marginTop: 8,
    gap: 3,
  },
  branchAnnotationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 10,
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 5,
  },
  branchAnnotationDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    flexShrink: 0,
  },
  branchAnnotationText: {
    fontSize: 11,
    color: C.brownMedium,
    fontStyle: 'italic',
    flex: 1,
  },
});

export default WorkbookMovesList;
